import React from 'react'
import { ArrowDown, ArrowUpRight, CircleDollarSign, Layers, PackageOpen, RefreshCw, Receipt, Tag } from 'lucide-react'
import { Bar, BarChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import { cn } from '../../../../lib/utils'
import type { KpiSummary, TransactionTrendRow } from '../ClientDashboardV3Performance'
import KpiHelpIcon, { type KpiTooltipKey } from './KpiHelpIcon'

type DeltaTone = 'success' | 'destructive' | 'info'

type MetricTrendPoint = {
  month: string;
  current: number;
  comparison: number;
}

export type PerformanceMetricCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  tone: DeltaTone;
  ly: string;
  trend: MetricTrendPoint[];
  trendLabel: string;
  planComparison: string;
  lastYearComparison: string;
}

export type PerformancePeriod = 'MTD' | 'Last 3M' | 'Last 12M'

export type TransactionalPrimaryCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  comparison: string;
  deltaClassName: string;
  trend: Array<{ month: string; value: number }>;
}

export type TransactionalCompactCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  deltaClassName: string;
}

function percentageDelta(current: number, compare: number) {
  if (!compare) return 0
  return ((current - compare) / Math.abs(compare)) * 100
}

function formatSignedPercent(value: number, digits = 1) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)}%`
}

function formatSignedPointDelta(value: number, digits = 1) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)} pts`
}

export function buildTransactionalPrimaryCards(rows: TransactionTrendRow[] = []): TransactionalPrimaryCard[] {
  const latest = rows[rows.length - 1]
  const unavailable = (label: string, icon: any): TransactionalPrimaryCard => ({
    label,
    icon,
    value: '—',
    delta: 'Shopify data unavailable',
    comparison: '',
    deltaClassName: 'text-gray-500',
    trend: [],
  })

  if (!latest) {
    return [
      unavailable('Total transactions', Receipt),
      unavailable('Average order value', CircleDollarSign),
      unavailable('Gross margin', ArrowUpRight),
    ]
  }

  const monthLabel = (value: string) => new Date(`${value}T00:00:00Z`).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  const trend = (value: (row: TransactionTrendRow) => number): Array<{ month: string; value: number }> => rows.map((row) => ({
    month: monthLabel(row.monthDate),
    value: Number(value(row)) || 0,
  }))
  const comparison = (current: number, lastYear: number, pointDelta = false) => {
    if (!lastYear) return { delta: 'LY unavailable', deltaClassName: 'text-gray-500' }
    const delta = pointDelta
      ? `${formatSignedPointDelta(current - lastYear)} vs LY`
      : `${formatSignedPercent(percentageDelta(current, lastYear))} vs LY`
    return {
      delta,
      deltaClassName: current >= lastYear ? 'text-emerald-600' : 'text-red-600',
    }
  }
  const transactionComparison = comparison(Number(latest.transactionCount) || 0, Number(latest.transactionCountLy) || 0)
  const aovComparison = comparison(Number(latest.averageOrderValue) || 0, Number(latest.averageOrderValueLy) || 0)
  const marginComparison = comparison(Number(latest.grossMarginPct) || 0, Number(latest.grossMarginPctLy) || 0, true)

  return [
    {
      label: 'Total transactions',
      icon: Receipt,
      value: new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(latest.transactionCount) || 0),
      comparison: latest.transactionCountLy ? `LY ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(latest.transactionCountLy) || 0)}` : '',
      trend: trend((row) => row.transactionCount),
      ...transactionComparison,
    },
    {
      label: 'Average order value',
      icon: CircleDollarSign,
      value: PerformanceDashboardService.moneyFormat(Number(latest.averageOrderValue) || 0, true),
      comparison: latest.averageOrderValueLy ? `LY ${PerformanceDashboardService.moneyFormat(Number(latest.averageOrderValueLy) || 0, true)}` : '',
      trend: trend((row) => row.averageOrderValue),
      ...aovComparison,
    },
    {
      label: 'Gross margin',
      icon: ArrowUpRight,
      value: formatPercent(Number(latest.grossMarginPct) || 0),
      comparison: latest.grossMarginPctLy ? `LY ${formatPercent(Number(latest.grossMarginPctLy) || 0)}` : '',
      trend: trend((row) => row.grossMarginPct),
      ...marginComparison,
    },
  ]
}

function formatPercent(value: number, digits = 1) {
  return `${value.toFixed(digits)}%`
}

export function buildTransactionalCompactCards(
  rowsOrSummary?: TransactionTrendRow[] | KpiSummary | null,
  maybeSummary?: KpiSummary | null,
): TransactionalCompactCard[] {
  const rows = Array.isArray(rowsOrSummary) ? rowsOrSummary : []
  const summary = (rowsOrSummary && !Array.isArray(rowsOrSummary)) ? (rowsOrSummary as KpiSummary) : (maybeSummary ?? null)

  const unavailable = (label: string, icon: any): TransactionalCompactCard => ({
    label,
    icon,
    value: '—',
    delta: 'Awaiting transaction feed',
    deltaClassName: 'text-gray-500',
  })

  const latest = rows[rows.length - 1]

  const markdownDelta = summary ? percentageDelta(summary.markdowns, summary.markdownsComp) : 0
  const turnDelta = summary ? summary.turn - summary.turnComp : 0
  const unitsDelta = latest ? percentageDelta(Number(latest.totalUnitsSold) || 0, Number(latest.totalUnitsSoldLy) || 0) : 0
  const unitsPerTransactionDelta = latest ? Number(latest.unitsPerTransaction) - Number(latest.unitsPerTransactionLy) : 0

  return [
    latest
      ? {
        label: 'Units / transaction',
        icon: Layers,
        value: Number(latest.unitsPerTransaction || 0).toFixed(1),
        delta: `${formatSignedPointDelta(unitsPerTransactionDelta)} vs LY`,
        deltaClassName: unitsPerTransactionDelta >= 0 ? 'text-emerald-600' : 'text-red-600',
      }
      : unavailable('Units / transaction', Layers),
    latest
      ? {
        label: 'Total units sold',
        icon: PackageOpen,
        value: new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(latest.totalUnitsSold) || 0),
        delta: `${formatSignedPercent(unitsDelta)} vs LY`,
        deltaClassName: unitsDelta >= 0 ? 'text-emerald-600' : 'text-red-600',
      }
      : unavailable('Total units sold', PackageOpen),
    summary
      ? {
        label: 'Markdowns',
        icon: Tag,
        value: PerformanceDashboardService.moneyFormat(summary.markdowns, true),
        delta: `${formatSignedPercent(markdownDelta)} vs LY`,
        deltaClassName: markdownDelta <= 0 ? 'text-emerald-600' : 'text-red-600',
      }
      : unavailable('Markdowns', Tag),
    summary
      ? {
        label: 'Turn',
        icon: RefreshCw,
        value: `${summary.turn.toFixed(1)}×`,
        delta: `${turnDelta >= 0 ? '+' : ''}${turnDelta.toFixed(1)} vs LY`,
        deltaClassName: turnDelta >= 0 ? 'text-emerald-600' : 'text-red-600',
      }
      : unavailable('Turn', RefreshCw),
  ]
}

const toneClasses: Record<DeltaTone, string> = {
  success: 'bg-emerald-50 text-emerald-700',
  destructive: 'bg-red-50 text-red-700',
  info: 'bg-blue-50 text-blue-700',
}

function TransactionalInlineTrend({ label, points }: { label: string; points: TransactionalPrimaryCard['trend'] }) {
  if (points.length === 0) return <div className="flex h-8 items-center text-[11px] text-gray-400">No monthly Shopify trend data available.</div>
  return <div className="h-8" aria-label={`${label} monthly trend`} role="img"><ResponsiveContainer width="100%" height="100%"><LineChart data={points} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}><Tooltip formatter={(value: number) => [Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }), label]} labelStyle={{ color: '#344054' }} /><Line type="monotone" dataKey="value" stroke="#7F56D9" strokeWidth={2} dot={false} activeDot={{ r: 3 }} /></LineChart></ResponsiveContainer></div>
}

function resolveTransactionalTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Total transactions':
      return 'totalTransactions'
    case 'Average order value':
      return 'averageOrderValue'
    case 'Gross margin':
      return 'grossMargin'
    case 'Units / transaction':
      return 'unitsPerTransaction'
    case 'Total units sold':
      return 'totalUnitsSold'
    case 'Markdowns':
      return 'markdowns'
    case 'Turn':
      return 'annualTurn'
    default:
      return null
  }
}

export function TransactionsProfitabilitySection({ rows, summary, loading, comingSoon = false }: { rows: TransactionTrendRow[]; summary: KpiSummary | null; loading: boolean; comingSoon?: boolean }) {
  const primaryCards = React.useMemo(() => buildTransactionalPrimaryCards(rows), [rows])
  const compactCards = React.useMemo(() => buildTransactionalCompactCards(rows, summary), [rows, summary])

  return <section className="overflow-hidden rounded-[14px] bg-white shadow-sm">
    <div className="px-5 pb-4 pt-5"><h2 className="text-[13px] font-semibold text-gray-900">Transactional Data</h2><p className="text-xs text-gray-500">Detailed transaction-level metrics</p></div>
    <div className="px-5 pb-6">{comingSoon ? <div className="rounded-[14px] border border-dashed border-gray-200 bg-gray-50 p-5 text-sm text-gray-600"><div className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-700">Coming soon</div><p className="mt-3 text-sm font-medium text-gray-900">Transactional Data is not live for this client yet.</p><p className="mt-1 text-sm leading-6 text-gray-600">This section will appear here once the transaction feed is enabled for this POS integration.</p></div> : loading ? <div className="rounded-[14px] border border-gray-200 bg-white p-4 text-sm text-gray-500">Loading transactional metrics…</div> : <><div className="grid gap-[10px] lg:grid-cols-3">{primaryCards.map((stat) => {
      const Icon = stat.icon
      const unavailable = stat.trend.length === 0
      const DeltaIcon = stat.delta.startsWith('-') ? ArrowDown : ArrowUpRight
      return <div key={stat.label} className="flex min-h-[153px] flex-col gap-1.5 rounded-[14px] border border-[#E5E7EB] bg-white p-4"><div className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-gray-500" strokeWidth={1.16} /><span className="text-xs font-medium text-gray-700">{stat.label}</span>{resolveTransactionalTooltipKey(stat.label) && <KpiHelpIcon tooltipKey={resolveTransactionalTooltipKey(stat.label) as KpiTooltipKey} />}</div><span className="mt-1 text-[30px] font-semibold leading-9 tracking-tight text-gray-900">{stat.value}</span><div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">{!unavailable && <DeltaIcon className={cn('h-3 w-3', stat.deltaClassName)} />}<span className={cn('text-xs font-semibold', stat.deltaClassName)}>{stat.delta}</span><span className="text-xs font-medium text-gray-500">{stat.comparison}</span></div><div className="mt-auto pt-1"><TransactionalInlineTrend label={stat.label} points={stat.trend} /></div></div>
    })}</div><div className="mt-5 grid gap-[10px] sm:grid-cols-2 xl:grid-cols-4">{compactCards.map((stat) => { const Icon = stat.icon; return <div key={stat.label} className="min-h-[77px] rounded-[14px] bg-[#F2F4F7] p-3"><div className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-gray-700" strokeWidth={1.16} /><span className="text-xs font-medium text-gray-700">{stat.label}</span>{resolveTransactionalTooltipKey(stat.label) && <KpiHelpIcon tooltipKey={resolveTransactionalTooltipKey(stat.label) as KpiTooltipKey} />}</div><div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5"><span className="text-lg font-semibold tracking-tight text-gray-900">{stat.value}</span><span className={cn('text-[11px] font-semibold', stat.deltaClassName)}>{stat.delta}</span></div></div> })}</div></>}</div>
  </section>
}

